// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — the three modes.
//
// A mode must change how the game PLAYS, not just a number. These change the two things every
// decision in the game runs through:
//
//   INQUIRY        the reference. Survive the hearing. A card is a weapon; its rank only matters
//                  because it is what somebody has to name to put you out.
//   ON THE RECORD  you score the RANK you are still holding when the hearing closes, and winning it
//                  outright is worth nothing extra. So a card is suddenly an ASSET: the Advocate and
//                  the Retrial stop being ways to hurt people and become ways to trade up, and
//                  surviving on a Bailiff is worth one point against somebody else's eight.
//   FULL BRIEF     you hold TWO pieces of testimony and draw to three. Every turn is a three-way
//                  choice instead of a two-way one, the Judge's forced discard bites far harder
//                  (you can be sitting on the Judge, the Advocate AND a Retrial at once), and you
//                  can finally carry a shield alongside the Verdict.
//
// modeOf() validates an id off the wire. `MODES[id] || DEFAULT` lets "constructor" through as a Mode
// of undefined fields, and an undefined deck reaches the dealer as NaN.

import { BRIEF, SMALL, WIDE, type DeckSpec } from './cards';

export type ModeId = 'inquiry' | 'record' | 'brief';

/** How a hearing pays out. */
export type Scoring = 'win' | 'rank';

export interface Mode {
  id: ModeId;
  name: string;
  blurb: string;
  minPlayers: number;
  maxPlayers: number;
  /** Cards held between turns. You always draw one and play one, so a turn sees handSize + 1. */
  handSize: number;
  scoring: Scoring;
  /**
   * Standing for taking the hearing by being the LAST ONE STANDING ('win' scoring only).
   *
   * Worth double the other ending on purpose. "The deck ran out, so whoever happens to hold the
   * biggest number wins" is the weakest moment in this shape — it is the one outcome nobody chose —
   * so it pays half. Playing for the clock is therefore a consciously worse line than closing the
   * hearing yourself, and the pressure is always to act.
   */
  lastAward: number;
  /** Standing for holding the strongest testimony when the docket runs dry ('win' scoring only). */
  deckAward: number;
  /**
   * Standing for a player who was put out and correctly backed the eventual winner from the gallery.
   *
   * It exists because an elimination game makes the loser watch, and on a phone that is the worst
   * thirty seconds in the game. Backing a rival is one tap, it uses the exact same deduction the
   * hand itself trains, and it is worth strictly less than taking the hearing — tests/balance.test.ts
   * asserts that going out early is still the worst thing that can happen to you.
   */
  galleryAward: number;
  /**
   * Standing that ends the match, BY SEAT COUNT.
   *
   * MEASURED, not chosen. A flat target of 10 ran a four-player Inquiry to fourteen hearings — six
   * or seven minutes of a game whose whole pitch is that a hearing takes ninety seconds — because
   * the standing on offer per round is divided among more people the more people there are. Scaling
   * the post with the table keeps every mode at five or six hearings at every seat count, which is
   * the length the sim says the match arc actually holds up for.
   */
  targetFor: (players: number) => number;
  /** Cards revealed face-up beside the docket at the start of a hearing. */
  openBurns: (players: number) => number;
  deckFor: (players: number) => DeckSpec;
}

/**
 * The heads-up deduction fix, and the only rule in the game that is conditional on seat count.
 *
 * Two players and a sixteen-card deck means one card is set aside face-down and the other fourteen
 * are all reachable — so a perfect counter would simply know the opponent's hand near the end, and
 * an imperfect one is guessing between too many. Turning three of them face-up at the start cuts the
 * unknown pool by a fifth and makes counting genuinely tractable inside a ninety-second hearing,
 * which is what turns a heads-up round from a coin flip into a game.
 */
const OPEN_BURNS_HEADS_UP = 3;
const twoPlayerBurns = (players: number): number => (players === 2 ? OPEN_BURNS_HEADS_UP : 0);

const standardDeck = (players: number): DeckSpec => (players >= 5 ? WIDE : SMALL);

/**
 * Where the post is set, by seat count.
 *
 * The constant on top is the number of hearings a match should last, times the standing a hearing
 * pays out in total — measured against the simulator until every mode at every seat count landed in
 * the five-to-seven range. `Math.max` keeps a six-player table from being decided by one hearing.
 */
const winTarget = (players: number): number => Math.max(4, Math.round(14 / players));
const rankTarget = (players: number): number => Math.max(14, Math.round(44 / Math.sqrt(players)));

export const MODES: Record<ModeId, Mode> = {
  inquiry: {
    id: 'inquiry',
    name: 'Inquiry',
    blurb: 'Be the last one standing, or hold the strongest testimony when the docket runs dry.',
    minPlayers: 2,
    maxPlayers: 6,
    handSize: 1,
    scoring: 'win',
    lastAward: 2,
    deckAward: 1,
    galleryAward: 1,
    targetFor: winTarget,
    openBurns: twoPlayerBurns,
    deckFor: standardDeck,
  },
  record: {
    id: 'record',
    name: 'On the Record',
    blurb: 'Winning pays nothing. You score the rank you are still holding when it closes.',
    minPlayers: 2,
    maxPlayers: 6,
    handSize: 1,
    scoring: 'rank',
    lastAward: 0,
    deckAward: 0,
    galleryAward: 2,
    targetFor: rankTarget,
    openBurns: twoPlayerBurns,
    deckFor: standardDeck,
  },
  brief: {
    id: 'brief',
    name: 'Full Brief',
    blurb: 'Two pieces of testimony in hand, three on your turn. Far more rope to hang yourself with.',
    minPlayers: 2,
    maxPlayers: 4,
    handSize: 2,
    scoring: 'win',
    lastAward: 2,
    deckAward: 1,
    galleryAward: 1,
    targetFor: winTarget,
    openBurns: () => 0,
    deckFor: () => BRIEF,
  },
};

export const MODE_IDS: ModeId[] = ['inquiry', 'record', 'brief'];
export const DEFAULT_MODE: ModeId = 'inquiry';

/** Validate an id that arrived over the wire or out of localStorage. Never index a raw string. */
export function modeOf(id: unknown): Mode {
  return typeof id === 'string' && Object.hasOwn(MODES, id) ? MODES[id as ModeId] : MODES[DEFAULT_MODE];
}

/** Clamp a seat count a mode can actually deal. A wire-supplied number must never reach the dealer raw. */
export const seatsFor = (mode: Mode, players: number): number =>
  Math.max(mode.minPlayers, Math.min(mode.maxPlayers, Math.floor(players) || mode.minPlayers));

/**
 * Which seat a roster index takes in a given hearing.
 *
 * Whoever acts first in a hearing holds a small structural edge — they are the only player who can
 * put somebody out before anybody has had a turn. Rotating the seats every hearing means that edge
 * lands on a different person each time rather than on the same one all evening, so a MATCH is fair
 * even where a single hearing is not quite. tests/balance.test.ts measures both.
 */
export const seatForRound = (rosterIndex: number, round: number, n: number): number =>
  (rosterIndex + round) % n;

/**
 * The seating for one hearing: the plain rotation, offset once per MATCH.
 *
 * Two things were measured to get here, and the first one was wrong.
 *
 * Acting first is a real structural advantage — you are the only player who can put somebody out
 * before they have had a turn — worth 54.5% to 58.3% of hearings at two players. The obvious fix is
 * to hand the opening to whoever is behind, and it made things WORSE: Full Brief at two players went
 * from 49.8/50.2 to 41.4/58.6. The catch-up rule interacts with its own tie-break — a player only
 * opens while they are behind, so the player who wins the TIED rounds banks a lead in exactly the
 * rounds the rule does not touch, and opens more often overall. This is the factory's recurring
 * lesson about draft-order fixes overshooting, reproduced from scratch. Do not reintroduce it.
 *
 * The second measurement found what was actually wrong. A three-player match runs about 4.3 hearings,
 * and a rotation with no offset always gives hearing 1 to the same roster index, so over four
 * hearings one player opens twice and another once: a sampling artifact of SHORT MATCHES, not a bias
 * in the game. Offsetting the whole rotation by a value derived from the match seed makes which
 * player opens first a fair coin per match, which removes it without touching a single rule.
 *
 * `offset` must be the same on every peer, so it is derived from the shared seed and nothing else.
 */
export function openingOrder<T>(roster: readonly T[], round: number, offset = 0): T[] {
  const n = roster.length;
  if (n === 0) return [];
  const out: T[] = [];
  for (let i = 0; i < n; i++) out.push(roster[(((i - round - offset) % n) + n) % n]);
  return out;
}

/**
 * The match's rotation offset. Shared seed in, same answer on every peer.
 *
 * A PROPER avalanche mix, and that is not fussiness. The first version was `floor(seed / 7) % n`,
 * which correlated with the stride of the seeds the simulator was walking and manufactured a
 * nine-point four-player "bias" that did not exist in the game at all. A weak hash here does not
 * make the game slightly less fair — it makes the MEASUREMENT lie, in a direction nobody would
 * think to check.
 */
export function matchOffset(seed: number, players: number): number {
  if (players <= 0) return 0;
  let x = (seed ^ 0x9e3779b9) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = (x ^ (x >>> 16)) >>> 0;
  return x % players;
}
